import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@neo/primitives/command'
import { Cross2Icon } from '@radix-ui/react-icons'
import { CheckIcon } from 'lucide-react'
import React, { type ForwardedRef } from 'react'
import { Virtuoso } from 'react-virtuoso'

interface SelectOption {
  value: string
  label: string | React.ReactNode
  searchValue?: string
  [key: string]: unknown
}

interface OptionItemProps {
  option: SelectOption
  value: string[]
  handleSelect: (value: string) => void
  multiple: boolean
}

interface TabContentProps {
  handleSelect: (value: string) => void
  value: string[]
  options: SelectOption[]
}

const OptionItem: React.FC<OptionItemProps> = ({ option, value, handleSelect, multiple }) => {
  const isSelected = value?.includes(option.value)

  return (
    <CommandItem onSelect={() => handleSelect(option.value)}>
      {multiple && (
        <div
          className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50'}`}
        >
          {isSelected && <CheckIcon />}
        </div>
      )}
      <span className="truncate">{option.label}</span>
    </CommandItem>
  )
}

export const TabContent = React.forwardRef<HTMLInputElement, TabContentProps>(
  ({ handleSelect, value, options }, ref: ForwardedRef<HTMLInputElement>) => {
    const [searchTerm, setSearchTerm] = React.useState<string>('')

    // Filter options based on search term
    const filteredOptions = options.filter((option) => {
      const searchText = searchTerm.toLowerCase()
      const label = typeof option.label === 'string' ? option.label.toLowerCase() : ''
      return label.includes(searchText) || option.searchValue?.toLowerCase().includes(searchText)
    })

    // Render list using Virtuoso if there are more than 6 options
    const comp =
      filteredOptions.length > 6 ? (
        <Virtuoso
          itemContent={(_, option) => (
            <OptionItem
              key={option.value}
              option={option}
              value={value}
              handleSelect={handleSelect}
              multiple
            />
          )}
          data={filteredOptions}
          style={{ height: 200 }}
        />
      ) : (
        filteredOptions.map((option) => (
          <OptionItem
            key={option.value}
            option={option}
            value={value}
            handleSelect={handleSelect}
            multiple
          />
        ))
      )

    return (
      <>
        <div className="relative">
          <CommandInput
            value={searchTerm}
            onValueChange={(e) => setSearchTerm(e)}
            ref={ref} // Forwarding the ref to the input
            placeholder="Search..."
            className="h-9"
          />
          {searchTerm && (
            <button
              aria-label="Clear search"
              type="button"
              className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchTerm('')}
            >
              <Cross2Icon className="size-4" />
            </button>
          )}
        </div>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandList>
          <CommandGroup>{comp}</CommandGroup>
        </CommandList>
      </>
    )
  },
)

TabContent.displayName = 'TabContent'
export default TabContent
